import { useEffect, useRef } from 'react';

/** Background refresh interval for doctor dashboard (new bookings, etc.) */
export const DOCTOR_POLL_MS = 20000;

/**
 * Poll + refresh when tab becomes visible or window gains focus.
 * @param {number|null} doctorId
 * @param {(doctorId: number, isBackground: boolean) => void} onRefresh
 * @param {{ enabled?: boolean, pollMs?: number }} options
 */
export function useDoctorLiveRefresh(doctorId, onRefresh, options = {}) {
  const { enabled = true, pollMs = DOCTOR_POLL_MS } = options;
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled || !doctorId) return;

    const run = () => refreshRef.current?.(doctorId, true);

    const intervalId = setInterval(run, pollMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [doctorId, enabled, pollMs]);
}

/**
 * Lighter poll for navbar notifications only.
 */
export function useIntervalCallback(callback, delayMs, enabled = true) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled || !delayMs) return;
    const id = setInterval(() => cbRef.current?.(), delayMs);
    return () => clearInterval(id);
  }, [delayMs, enabled]);
}
